/**
 * Community Reports - User-submitted safety reports
 * Submit and view crowdsourced safety information
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertTriangle,
  Shield,
  Car,
  Users,
  MapPin,
  Clock,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Plus,
  Loader2,
  CheckCircle,
  XCircle,
  Target,
  Send,
  Eye,
  Flame,
  Zap,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configurations
const CATEGORIES = {
  crime: {
    icon: Shield,
    color: 'bg-red-500',
    label: 'Crime & Safety',
    types: [
      { value: 'theft', label: 'Theft' },
      { value: 'vandalism', label: 'Vandalism' },
      { value: 'assault', label: 'Assault' },
      { value: 'suspicious_activity', label: 'Suspicious Activity' },
      { value: 'break_in', label: 'Break-in' },
      { value: 'other_crime', label: 'Other Crime' }
    ]
  },
  hazard: {
    icon: AlertTriangle,
    color: 'bg-orange-500',
    label: 'Hazards',
    types: [
      { value: 'road_hazard', label: 'Road Hazard' },
      { value: 'downed_tree', label: 'Downed Tree' },
      { value: 'flooding', label: 'Flooding' },
      { value: 'fire', label: 'Fire' },
      { value: 'gas_leak', label: 'Gas Leak' },
      { value: 'power_outage', label: 'Power Outage' },
      { value: 'other_hazard', label: 'Other Hazard' }
    ]
  },
  traffic: {
    icon: Car,
    color: 'bg-amber-500',
    label: 'Traffic',
    types: [
      { value: 'accident', label: 'Accident' },
      { value: 'road_closure', label: 'Road Closure' },
      { value: 'heavy_traffic', label: 'Heavy Traffic' },
      { value: 'construction', label: 'Construction' },
      { value: 'broken_signal', label: 'Broken Signal' }
    ]
  },
  community: {
    icon: Users,
    color: 'bg-blue-500',
    label: 'Community',
    types: [
      { value: 'noise_complaint', label: 'Noise Complaint' },
      { value: 'parking_issue', label: 'Parking Issue' },
      { value: 'homeless_encampment', label: 'Homeless Encampment' },
      { value: 'animal_concern', label: 'Animal Concern' },
      { value: 'other', label: 'Other' }
    ]
  }
};

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-green-500' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' }
];

const ReportCard = ({ report, onVote, onComment }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  
  const category = CATEGORIES[report.category] || CATEGORIES.community;
  const Icon = category.icon;

  const handleSubmitComment = () => {
    if (commentText.trim()) {
      onComment?.(report.id, commentText);
      setCommentText('');
    }
  };

  return (
    <div 
      className="p-4 rounded-lg border border-slate-700 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
      data-testid={`report-card-${report.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${category.color}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div>
            <Badge variant="outline" className="text-[10px] mb-1">
              {report.type?.replace(/_/g, ' ')}
            </Badge>
            <h4 className="text-sm font-medium text-slate-200">{report.title}</h4>
          </div>
        </div>
        <Badge 
          className={`${SEVERITY_OPTIONS.find(s => s.value === report.severity)?.color || 'bg-slate-500'} text-white text-[10px]`}
        >
          {report.severity}
        </Badge>
      </div>

      {/* Description */}
      <p className="text-xs text-slate-400 mb-3 line-clamp-3">{report.description}</p>

      {/* Location & Time */}
      <div className="flex items-center gap-4 text-[10px] text-slate-500 mb-3">
        {report.location?.address && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {report.location.address}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {report.time_ago}
        </span>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2 mb-3">
        {report.status === 'verified' && (
          <Badge variant="outline" className="text-[10px] border-green-500 text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Verified
          </Badge>
        )}
        {report.status === 'pending' && (
          <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-400">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        )}
        {report.status === 'resolved' && (
          <Badge variant="outline" className="text-[10px] border-slate-500 text-slate-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Resolved
          </Badge>
        )}
        {report.anonymous && (
          <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-500">
            Anonymous
          </Badge>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-700">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onVote?.(report.id, true)}
            className="text-slate-400 hover:text-green-400 h-8 px-2"
          >
            <ThumbsUp className="w-3.5 h-3.5 mr-1" />
            <span className="text-xs">{report.votes?.helpful || 0}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onVote?.(report.id, false)}
            className="text-slate-400 hover:text-red-400 h-8 px-2"
          >
            <ThumbsDown className="w-3.5 h-3.5 mr-1" />
            <span className="text-xs">{report.votes?.not_helpful || 0}</span>
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowComments(!showComments)}
          className="text-slate-400 hover:text-teal-400 h-8 px-2"
        >
          <MessageSquare className="w-3.5 h-3.5 mr-1" />
          <span className="text-xs">{report.comments?.length || 0}</span>
        </Button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          {report.comments?.length > 0 && (
            <div className="space-y-2 mb-3">
              {report.comments.map((comment, i) => (
                <div key={i} className="p-2 rounded bg-slate-800/50 text-xs">
                  <p className="text-slate-300">{comment.text}</p>
                  <p className="text-slate-500 text-[10px] mt-1">
                    {comment.anonymous ? 'Anonymous' : 'User'} • {new Date(comment.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="text-xs bg-slate-800/50 border-slate-700"
            />
            <Button
              size="sm"
              onClick={handleSubmitComment}
              disabled={!commentText.trim()}
              className="bg-teal-600 hover:bg-teal-500"
            >
              <Send className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const SubmitReportForm = ({ onSubmit, onCancel }) => {
  const [category, setCategory] = useState('');
  const [reportType, setReportType] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [location, setLocation] = useState({ lat: '', lng: '', address: '' });
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedCategory = CATEGORIES[category];

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation(prev => ({
            ...prev,
            lat: pos.coords.latitude.toFixed(6),
            lng: pos.coords.longitude.toFixed(6)
          }));
        },
        () => setError('Could not get your location')
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category || !reportType || !title || !description) {
      setError('Please fill in all required fields');
      return;
    }

    if (!location.lat || !location.lng) {
      setError('Please provide a location');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        category,
        report_type: reportType,
        title,
        description,
        severity,
        location: {
          lat: parseFloat(location.lat),
          lng: parseFloat(location.lng),
          address: location.address
        },
        anonymous
      });
    } catch (err) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="submit-report-form">
      {/* Category Selection */}
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                setCategory(key);
                setReportType('');
              }}
              className={`p-3 rounded-lg border ${
                category === key 
                  ? `${cat.color} border-transparent` 
                  : 'border-slate-700 bg-slate-800/50 hover:bg-slate-800'
              } transition-colors`}
            >
              <Icon className={`w-5 h-5 mx-auto mb-1 ${category === key ? 'text-white' : 'text-slate-400'}`} />
              <p className={`text-xs ${category === key ? 'text-white' : 'text-slate-400'}`}>
                {cat.label}
              </p>
            </button>
          );
        })}
      </div>

      {/* Report Type */}
      {selectedCategory && (
        <Select value={reportType} onValueChange={setReportType}>
          <SelectTrigger className="bg-slate-800/50 border-slate-700">
            <SelectValue placeholder="Select type..." />
          </SelectTrigger>
          <SelectContent>
            {selectedCategory.types.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Title */}
      <Input
        placeholder="Brief title for your report..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        className="bg-slate-800/50 border-slate-700"
      />

      {/* Description */}
      <Textarea
        placeholder="Describe what you observed in detail..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={2000}
        rows={4}
        className="bg-slate-800/50 border-slate-700 resize-none"
      />

      {/* Severity */}
      <div>
        <label className="text-xs text-slate-400 mb-2 block">Severity Level</label>
        <div className="flex gap-2">
          {SEVERITY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSeverity(opt.value)}
              className={`flex-1 py-2 px-3 rounded-lg border text-xs ${
                severity === opt.value
                  ? `${opt.color} border-transparent text-white`
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:bg-slate-800'
              } transition-colors`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="text-xs text-slate-400 mb-2 block">Location</label>
        <div className="space-y-2">
          <Input
            placeholder="Address (optional)"
            value={location.address}
            onChange={(e) => setLocation(prev => ({ ...prev, address: e.target.value }))}
            className="bg-slate-800/50 border-slate-700"
          />
          <div className="flex gap-2">
            <Input
              placeholder="Latitude"
              value={location.lat}
              onChange={(e) => setLocation(prev => ({ ...prev, lat: e.target.value }))}
              className="bg-slate-800/50 border-slate-700"
            />
            <Input
              placeholder="Longitude"
              value={location.lng}
              onChange={(e) => setLocation(prev => ({ ...prev, lng: e.target.value }))}
              className="bg-slate-800/50 border-slate-700"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleUseCurrentLocation}
              className="px-3"
            >
              <Target className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Anonymous */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="anonymous"
          checked={anonymous}
          onCheckedChange={setAnonymous}
        />
        <label htmlFor="anonymous" className="text-xs text-slate-400 cursor-pointer">
          Submit anonymously
        </label>
      </div>

      {error && (
        <div className="p-2 rounded bg-red-500/20 border border-red-500/50 text-red-400 text-xs">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading || !category || !reportType || !title || !description}
          className="flex-1 bg-teal-600 hover:bg-teal-500"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Report
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export const CommunityReportsPanel = () => {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');

  const fetchReports = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/safety/community-reports?hours=48&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/safety/community-reports/stats?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [fetchReports, fetchStats]);

  const handleSubmitReport = async (reportData) => {
    const response = await fetch(`${API_URL}/api/safety/community-reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(reportData)
    });

    if (!response.ok) throw new Error('Failed to submit report');
    
    setShowSubmitForm(false);
    fetchReports();
    fetchStats();
  };

  const handleVote = async (reportId, helpful) => {
    try {
      await fetch(`${API_URL}/api/safety/community-reports/${reportId}/vote?helpful=${helpful}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchReports();
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleComment = async (reportId, comment) => {
    try {
      await fetch(`${API_URL}/api/safety/community-reports/${reportId}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ comment, anonymous: false })
      });
      fetchReports();
    } catch (error) {
      console.error('Failed to comment:', error);
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="community-reports-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-400" />
            Community Reports
          </h3>
          <Button
            size="sm"
            onClick={() => setShowSubmitForm(true)}
            className="bg-teal-600 hover:bg-teal-500"
          >
            <Plus className="w-4 h-4 mr-1" />
            Report
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(CATEGORIES).slice(0, 4).map(([key, cat]) => {
              const count = stats.by_category?.[key] || 0;
              return (
                <div key={key} className="text-center p-2 rounded-lg bg-slate-800/50">
                  <p className="text-lg font-bold text-white">{count}</p>
                  <p className="text-[10px] text-slate-400">{cat.label}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      {showSubmitForm ? (
        <ScrollArea className="flex-1 p-4">
          <SubmitReportForm
            onSubmit={handleSubmitReport}
            onCancel={() => setShowSubmitForm(false)}
          />
        </ScrollArea>
      ) : (
        <ScrollArea className="flex-1 p-4">
          {loading && reports.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
            </div>
          ) : reports.length > 0 ? (
            <div className="space-y-3">
              {reports.map(report => (
                <ReportCard
                  key={report.id}
                  report={report}
                  onVote={handleVote}
                  onComment={handleComment}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No community reports yet</p>
              <p className="text-xs text-slate-500">Be the first to report something!</p>
            </div>
          )}
        </ScrollArea>
      )}
    </div>
  );
};

// Dialog wrapper
export const CommunityReportsDialog = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <Users className="w-4 h-4" />
            Community
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg h-[85vh] p-0 glass-card border-slate-800">
        <CommunityReportsPanel />
      </DialogContent>
    </Dialog>
  );
};

export default CommunityReportsPanel;
